import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

import { shouldSkipTranslation } from '@/shared/utils/translationHelpers';
import useApi from '@/shared/composables/useApi';

/**
 * Composable to translate BC text content.
 *
 * @returns {{
 *   $bct: (inputText: String) => {value: string},
 * }}
 */
export default function useTranslator() {
  const { api } = useApi();
  const store = useStore();
  const i18n = useI18n();
  const apiGatewayAuthKey = store.state.config?.env?.VUE_APP_API_GATEWAY_AUTH_KEY;
  const apiGatewayUrl = store.state.config?.env?.VUE_APP_API_GATEWAY_URL;

  // Create a single watcher for locale changes that will trigger re-translation
  // of all active translations
  const activeTranslations = ref(new Map());

  watch(
    () => i18n.locale.value,
    () => {
      // Re-translate all active translations when locale changes
      activeTranslations.value.forEach((translatedTextRef, inputText) => {
        void getTranslatedText(inputText, translatedTextRef);
      });
    },
    { immediate: true },
  );

  async function getTranslatedText(inputText, translatedTextRef) {
    const isEnglish = i18n.locale.value === 'en' || i18n.locale.value === i18n.fallbackLocale.value;
    const isNonEmptyString = typeof inputText === 'string' && inputText.trim() !== '';
    if (isEnglish || !isNonEmptyString) {
      translatedTextRef.value = inputText;
      return;
    }

    // We already have an i18n translation if the 'i18n.messages' either:
    //   1. includes a key (for english) or
    //   2. includes a value (for anything other than english)
    //    ...that matches the input text
    const hasExistingTranslation =
      Object.keys(i18n.messages.value).length > 0 &&
      i18n.messages.value[i18n.locale.value] &&
      (Object.keys(i18n.messages.value[i18n.locale.value]).includes(inputText) ||
        Object.values(i18n.messages.value[i18n.locale.value]).includes(inputText));

    if (hasExistingTranslation) {
      translatedTextRef.value = i18n.t(inputText);
      return;
    }

    // If there is no local translation,
    //  1. Return input text if automatic translations not enabled
    //  2. Return API translation if available, otherwise input text
    if (!store.state.config?.enable_automatic_translations) {
      translatedTextRef.value = inputText;
      return;
    }

    const apiTranslation = await fetchTranslation(inputText);
    translatedTextRef.value = apiTranslation || inputText;
  }

  function translateText(inputText) {
    // Check if we already have a translation for this text
    if (activeTranslations.value.has(inputText)) {
      return activeTranslations.value.get(inputText);
    }

    const translatedText = ref(inputText);

    // Add this translation to the active translations map
    activeTranslations.value.set(inputText, translatedText);

    // Initial translation
    void getTranslatedText(inputText, translatedText);

    return translatedText;
  }

  async function fetchTranslation(inputText) {
    // Check if the text should be skipped for translation
    if (shouldSkipTranslation(inputText)) {
      return inputText;
    }

    try {
      const translationResult = await api.translations.getTranslation(
        getValidLocaleCode(i18n.locale.value),
        inputText,
        apiGatewayUrl,
        apiGatewayAuthKey,
      );

      if (translationResult.success) {
        i18n.setLocaleMessage(i18n.locale.value, {
          ...i18n.messages.value[i18n.locale.value],
          [inputText]: translationResult.translatedString,
        });
        return translationResult.translatedString;
      }
      return inputText;
    } catch (error) {
      return inputText;
    }
  }

  // This wrapper allows us to preserve reactivity of the translated text
  // but returns the translated text as a string instead of a ref which
  // makes access uniform for implementation.
  // Also fixes issue where direct access was causing a problem, with Vue's
  // ref unwrapping in the template.
  function translationWrapper(stringValue) {
    return translateText(stringValue).value;
  }

  function getValidLocaleCode(locale) {
    if (locale === 'zhcht') return 'zh-Hant'; // Traditional Chinese - 'zhcht' is a legacy code
    return locale;
  }

  // $bct -> bridge care translation
  return {
    $bct: translationWrapper,
    translateText: translateText,
    testGetValidLocaleCode: getValidLocaleCode,
  };
}
